// Sampling: the ONE place where Lab dip, Sample, Fit sample, PPS and the bulk fabric test are changed. One row per PO line, one
// column per stage; a tick box in every status cell (the whole cell is clickable), a tick box in each stage heading for
// every line shown, and one bar: New status, Date, Comment (needed to reject) and "Apply to N". Different stages on
// different lines are applied together. Clicking a status (not the tick area) opens that stage's rounds (with "Change
// status") or, for the bulk fabric test, the lots panel — directly under the table. The PO tabs and the Time & Action Plan
// only show what is stored here.
import { ApiError, closeModal, confirmBox, esc, formError, formatDate, formatDateTime, get, openModal, orgToday, post, toast } from "../lib.js";
import { featureOn, onAction, onChange, onForm } from "../state.js";
import { filterPanelHtml } from "../filterpanel.js";
import { renderLotsPanel } from "./fabriclots.js";
import { renderLabDipPanel } from "./labdips.js";

type Stage = "labdip" | "sample" | "fit" | "pps" | "fabric";
const STAGES: Stage[] = ["labdip", "sample", "fit", "pps", "fabric"];
/** The stage columns shown: Bulk fabric test only when the organisation makes workwear (hidden, never deleted). */
const shownStages = () => STAGES.filter((st) => st !== "fabric" || featureOn("fabricTest"));
const TONES = ["ok", "bad", "amber", "grey"];
const MIXED: Array<[string, string]> = [["approved", "Approved / Pass"], ["rejected", "Rejected / Fail"]];

let host: HTMLElement | null = null;
let data: any = null;
let sel = new Set<string>();                 // "lineId:stage"
let filt = { po: "", q: "", completed: false };
let open: { lineId: string; stage: Stage } | null = null;
let openSample: ((lineId: string, type: string) => void) | null = null;

const key = (lineId: string, stage: string) => `${lineId}:${stage}`;
const stageName = (st: Stage) => (st === "labdip" ? "Lab dip" : st === "fabric" ? "Bulk fabric test" : data?.names?.[st] || { sample: "Sample", fit: "Fit sample", pps: "PPS" }[st]);
const rowOf = (lineId: string) => data?.rows.find((r: any) => r.lineId === lineId);

/** Mount the table in the Sampling page. `po`, `line` and `stage` come from the address (a PO tab or the Time & Action Plan). */
export async function mountSamplingGrid(el: HTMLElement, opts: { po?: string; line?: string; stage?: string; openLine: (lineId: string, type: string) => void }) {
  host = el; openSample = opts.openLine;
  pageFirst(el);
  filt = { po: opts.po || "", q: "", completed: false };
  sel = new Set(); open = null;
  await load();
  if (opts.line) {
    const r = rowOf(opts.line);
    if (r && !filt.po) filt.po = r.poId;
    draw();
    const tr = host.querySelector<HTMLElement>(`tr[data-line="${CSS.escape(opts.line)}"]`);
    tr?.scrollIntoView({ block: "center" });
    if (r && opts.stage && STAGES.includes(opts.stage as Stage) && r.cells[opts.stage]?.applies) await openPanel(opts.line, opts.stage as Stage);
  }
}
async function load() {
  if (!host) return;
  try { data = await get("/api/org/sampling/grid"); } catch (err) { host.innerHTML = `<div class="empty">${esc((err as Error).message)}</div>`; return; }
  for (const k of [...sel]) { const [l, s] = k.split(":"); if (!rowOf(l)?.cells[s]?.tick) sel.delete(k); }
  draw();
}
const canTick = () => !!(data?.canManage || data?.canFabric);

/** The lines shown, grouped per PO (each PO's lines together, in the order the POs first appear; lines keep their order). */
function shown(): any[] {
  const q = filt.q.trim().toLowerCase();
  const rows = data.rows.filter((r: any) => (filt.po ? r.poId === filt.po : filt.completed || r.open)
    && (!q || `${r.poNumber} ${r.code} ${r.colour} ${r.description} ${r.supplier}`.toLowerCase().includes(q)));
  const first = new Map<string, number>();
  rows.forEach((r: any, i: number) => { if (!first.has(r.poId)) first.set(r.poId, i); });
  return rows.map((r: any, i: number) => ({ r, i })).sort((a: any, b: any) => first.get(a.r.poId)! - first.get(b.r.poId)! || a.i - b.i).map((x: any) => x.r);
}
/** Where each row sits in its PO group: the first line of a PO starts the group (a divider and the line count). */
function groups(rows: any[]): Array<{ first: boolean; count: number; band: number }> {
  const count = new Map<string, number>();
  rows.forEach((r: any) => count.set(r.poId, (count.get(r.poId) || 0) + 1));
  let last = "", band = -1;
  return rows.map((r: any) => { const first = r.poId !== last; if (first) band++; last = r.poId; return { first, count: count.get(r.poId)!, band }; });
}

function cell(r: any, st: Stage): string {
  const c = r.cells[st], b = r.badges?.[st];
  if (!c?.applies || !b) return `<td class="sg-na" title="${esc(c?.why || "Not needed for this line")}">—</td>`;
  const k = key(r.lineId, st), on = sel.has(k), isOpen = open?.lineId === r.lineId && open?.stage === st;
  const tone = TONES.includes(b.tone) ? b.tone : "grey";
  const late = st === "labdip" && !!b.due && b.tone !== "ok" && b.due < orgToday();
  const badge = `<button type="button" class="sg-badge cp-badge tone-${tone} ${isOpen ? "sg-open" : ""} ${late ? "sg-late" : ""}" data-action="sg-open" data-line="${esc(r.lineId)}" data-stage="${st}" title="${esc(`${b.title}${late ? " · overdue" : ""}. Click to see ${st === "fabric" ? "the lots" : "every round"}.`)}" aria-expanded="${isOpen}">${esc(b.text)}</button>`;
  if (!c.tick) return `<td class="sg-cell" title="${esc(c.why)}">${badge}</td>`;
  return `<td class="sg-cell sg-tickable ${on ? "sg-sel" : ""}" data-action="sg-cell" data-key="${esc(k)}"><input type="checkbox" class="sg-tick" ${on ? "checked" : ""} aria-label="${esc(`Tick ${stageName(st)} for ${r.poNumber} ${r.code} ${r.colour || ""}`)}">${badge}</td>`;
}

/**
 * Scrolling down over the table moves the PAGE first, until the table box fills the screen under the header; then the
 * table itself scrolls (and, at its end, the page again — the browser's own chaining). Up: the table first, then the
 * page. Mouse wheel / trackpad and a finger alike. Only the Sampling table's own box is involved.
 */
function pageFirst(el: HTMLElement) {
  const room = (box: HTMLElement) => {
    const top = Math.max(0, document.querySelector(".topbar")?.getBoundingClientRect().bottom || 0);
    const left = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    return Math.min(box.getBoundingClientRect().top - top, left);
  };
  el.addEventListener("wheel", (e) => {
    const box = (e.target as HTMLElement).closest?.<HTMLElement>(".sg-scroll");
    if (!box || e.deltaY <= 0 || e.ctrlKey || e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    const r = room(box);
    if (r > 1) { e.preventDefault(); window.scrollBy(0, Math.min(r, e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY)); }
  }, { passive: false });
  let y = 0, x = 0, mode: "" | "page" | "box" = "";
  el.addEventListener("touchstart", (e) => { if (e.touches.length === 1) { y = e.touches[0].clientY; x = e.touches[0].clientX; mode = ""; } }, { passive: true });
  el.addEventListener("touchmove", (e) => {
    const box = (e.target as HTMLElement).closest?.<HTMLElement>(".sg-scroll");
    if (!box || e.touches.length !== 1) return;
    const t = e.touches[0], dy = y - t.clientY, dx = x - t.clientX;
    if (!mode) mode = Math.abs(dy) > Math.abs(dx) && dy > 0 && room(box) > 1 ? "page" : "box";
    if (mode !== "page") return;
    const r = room(box);
    if (r <= 1) { mode = "box"; return; }
    e.preventDefault();
    window.scrollBy(0, Math.min(r, dy));
    y = t.clientY; x = t.clientX;
  }, { passive: false });
}

function draw() {
  if (!host || !data) return;
  const rows = shown(), tick = canTick(), grp = groups(rows);
  const head = (st: Stage) => {
    if (!tick) return `<th>${esc(stageName(st))}</th>`;
    const keys = rows.filter((r: any) => r.cells[st]?.tick).map((r: any) => key(r.lineId, st));
    return `<th class="sg-colhead"><label><input type="checkbox" data-action="sg-col" data-stage="${st}" ${keys.length && keys.every((k: string) => sel.has(k)) ? "checked" : ""} ${keys.length ? "" : "disabled"} aria-label="${esc(`Tick ${stageName(st)} for every line shown`)}"> ${esc(stageName(st))}</label></th>`;
  };
  host.innerHTML = `<div class="sg-filters">
<label class="field sg-search"><span>Search</span><input type="search" data-change="sg-q" value="${esc(filt.q)}" placeholder="PO, product code, colour, supplier"></label>
${filterPanelHtml({ id: "sampling", changed: (filt.po ? 1 : 0) + (filt.completed ? 1 : 0), resetAction: "sg-filters-reset", shown: `${rows.length} line${rows.length === 1 ? "" : "s"} shown`, fields: `
<label class="std-fp-field"><span>PO</span><select data-change="sg-po"><option value="">All POs</option>${data.pos.map((p: any) => `<option value="${esc(p.id)}" ${p.id === filt.po ? "selected" : ""}>${esc(p.number)}</option>`).join("")}</select></label>
<label class="check-row cp-fp-row"><input type="checkbox" data-change="sg-completed" ${filt.completed ? "checked" : ""}><span>Show completed and cancelled orders</span></label>` })}
</div>
${data.lastBulk ? `<div class="form-message ps-result sg-undo" role="status"><strong>${esc(data.lastBulk.summary)}</strong> <span class="muted">by ${esc(data.lastBulk.by)}, ${esc(formatDateTime(data.lastBulk.at))}</span> <button type="button" class="secondary small-btn" data-action="sg-undo" data-id="${esc(data.lastBulk.id)}">Undo</button></div>` : ""}
${tick ? `<form class="ps-bar sg-bar" data-form="sg-apply" novalidate>
  <label class="field"><span>New status</span><select name="status"></select></label>
  <label class="field"><span>Date</span><input type="date" name="date"></label>
  <label class="field ps-comment"><span>Comment <small class="muted">(required to reject)</small></span><input name="comment" maxlength="2000" placeholder="e.g. Approved by buyer"></label>
  <button type="submit" class="primary sg-apply">Apply to 0</button>
  <button type="button" class="secondary sg-clear" data-action="sg-clear" hidden>Clear</button>
  <div class="ps-msg sg-msg" role="alert" hidden></div>
</form>` : `<p class="muted sg-viewonly">View only: you can see every status; ask an Admin for Full access to Sampling to change them.</p>`}
<div class="sg-scroll" tabindex="0"><table class="sg-grid"><thead><tr><th class="sg-f1">PO#</th><th class="sg-f2">Product Code</th><th>Colour</th><th>Production route</th>${shownStages().map(head).join("")}<th>Description</th><th>Supplier</th><th>${esc(data.names?.seal || "Gold seal")}</th><th>Next action</th></tr></thead><tbody>
${rows.map((r: any, i: number) => `<tr data-line="${esc(r.lineId)}" data-po="${esc(r.poId)}" class="sg-band-${grp[i].band % 2} ${grp[i].first ? "sg-po-first" : ""} ${open?.lineId === r.lineId ? "sg-focus" : ""}"><td class="sg-f1"><a href="/app/po/${esc(r.poId)}" data-link class="${grp[i].first ? "" : "sg-po-repeat"}" title="${esc(`Open PO ${r.poNumber}${grp[i].count > 1 ? ` · ${grp[i].count} lines` : ""}`)}"><strong>${esc(r.poNumber)}</strong></a>${grp[i].first && grp[i].count > 1 ? `<small class="sg-po-count">${grp[i].count} lines</small>` : ""}</td>
<td class="sg-f2" title="${esc(r.description || "")}"><strong>${esc(r.code)}</strong></td><td>${esc(r.colour || "")}</td>
<td>${r.route === "STP" ? '<span class="cp-badge tone-grey">STP</span>' : `<span class="muted">${esc(r.route)}</span>`}</td>
${shownStages().map((st) => cell(r, st)).join("")}
<td class="sg-desc">${esc(r.description || "")}</td><td>${esc(r.supplier || "—")}</td>
<td>${r.goldSeal ? `<span class="cp-badge tone-ok">${esc(r.goldSeal.number)}</span>` : r.stp ? `<span class="cp-badge tone-grey">STP · ${esc(r.stp.sealNumber)}</span>` : '<span class="muted">—</span>'}</td>
<td>${r.next ? `<strong>${esc(r.next)}</strong>` : '<span class="muted">—</span>'}${r.locked ? '<small class="muted sample-sub">Bulk started · samples locked</small>' : ""}</td></tr>`).join("")
|| `<tr><td colspan="${shownStages().length + 8}"><div class="empty">${data.rows.length ? "No lines match. Clear the filters to see more." : "No garment or fabric lines on purchase orders yet."}</div></td></tr>`}
</tbody></table></div>
<div id="sg-panel"></div>`;
  refreshBar();
  if (open) { const o = open; void openPanel(o.lineId, o.stage, true); }
}

/**
 * The ticked stages decide the status list: one stage kind → its own list; lab dips with samples → the lab dip list
 * (samples have one more, In review); the fabric test with anything else → the statuses they share.
 */
function statusChoices(): Array<[string, string]> {
  const kinds = new Set([...sel].map((k) => (k.endsWith(":fabric") ? "fabric" : k.endsWith(":labdip") ? "labdip" : "sample")));
  if (kinds.has("fabric")) return kinds.size > 1 ? MIXED : data.statuses.fabric;
  return kinds.has("labdip") ? data.statuses.labdip : data.statuses.sample;
}
function refreshBar() {
  if (!host) return;
  const form = host.querySelector<HTMLFormElement>("form[data-form=sg-apply]");
  if (form) {
    const sel2 = form.elements.namedItem("status") as HTMLSelectElement, was = sel2.value || "approved";
    const list = statusChoices();
    sel2.innerHTML = list.map(([k, l]) => `<option value="${esc(k)}" ${k === was ? "selected" : ""}>${esc(l)}</option>`).join("");
    if (!list.some(([k]) => k === was)) sel2.value = list.some(([k]) => k === "approved") ? "approved" : list[0][0];
    form.querySelector(".sg-apply")!.textContent = `Apply to ${sel.size}`;
    form.querySelector<HTMLElement>(".sg-clear")!.hidden = !sel.size;
  }
  host.querySelectorAll<HTMLTableCellElement>("td.sg-tickable").forEach((td) => {
    const on = sel.has(td.dataset.key!);
    td.classList.toggle("sg-sel", on);
    td.querySelector<HTMLInputElement>(".sg-tick")!.checked = on;
  });
  const rows = shown();
  host.querySelectorAll<HTMLInputElement>("input[data-action=sg-col]").forEach((i) => {
    const keys = rows.filter((r: any) => r.cells[i.dataset.stage!]?.tick).map((r: any) => key(r.lineId, i.dataset.stage!));
    i.checked = !!keys.length && keys.every((k: string) => sel.has(k));
  });
}
const message = (t: string) => { const m = host?.querySelector<HTMLElement>(".sg-msg"); if (m) { m.textContent = t; m.hidden = !t; } };

onAction("sg-cell", (el, e) => {
  const k = el.dataset.key!;
  const t = e?.target as HTMLElement | undefined;
  const on = t instanceof HTMLInputElement ? t.checked : !sel.has(k);
  if (on) sel.add(k); else sel.delete(k);
  message("");
  refreshBar();
});
onAction("sg-col", (el) => {
  const st = el.dataset.stage!, on = (el as HTMLInputElement).checked;
  for (const r of shown()) if (r.cells[st]?.tick) { if (on) sel.add(key(r.lineId, st)); else sel.delete(key(r.lineId, st)); }
  message("");
  refreshBar();
});
onAction("sg-clear", () => { sel.clear(); message(""); refreshBar(); });
onChange("sg-po", (el) => {
  filt.po = (el as HTMLSelectElement).value;
  history.replaceState(null, "", filt.po ? `/app/sampling?po=${filt.po}` : "/app/sampling");
  draw();
});
onChange("sg-q", (el) => { filt.q = (el as HTMLInputElement).value; draw(); host?.querySelector<HTMLInputElement>("input[data-change=sg-q]")?.focus(); });
onChange("sg-completed", (el) => { filt.completed = (el as HTMLInputElement).checked; draw(); });
onAction("sg-filters-reset", () => { filt.po = ""; filt.completed = false; history.replaceState(null, "", "/app/sampling"); draw(); });

/** One warning listing every line being passed without a test report or lot details: Continue or Cancel. */
function warnNoReport(text: string, lines: string[]): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "confirm-overlay falcon-confirm";
    overlay.innerHTML = `<div class="confirm-box sg-warn" role="alertdialog" aria-modal="true"><h3>${esc(text)}</h3>${lines.length ? `<ul>${lines.map((l) => `<li>${esc(l)}</li>`).join("")}</ul>` : ""}
<div class="confirm-actions"><button type="button" class="secondary" data-v="no">Cancel</button><button type="button" class="primary" data-v="yes">Continue</button></div></div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener("click", (e) => { const v = (e.target as HTMLElement).closest<HTMLElement>("[data-v]")?.dataset.v; if (v) { overlay.remove(); resolve(v === "yes"); } });
    overlay.querySelector<HTMLButtonElement>("[data-v=yes]")?.focus();
  });
}

onForm("sg-apply", async (form) => {
  if (!data) return;
  message("");
  const v = new FormData(form);
  const status = String(v.get("status") || ""), comment = String(v.get("comment") || "").trim();
  if (!sel.size) return message("Tick at least one status");
  if (status === "rejected" && !comment) return message("Enter a comment to reject");
  const cells = [...sel].map((k) => { const [lineId, stage] = k.split(":"); return { lineId, stage }; });
  const pps = cells.filter((c) => c.stage === "pps").length;
  if (data.confirmBulkPps && pps > 1 && status === "approved" && !(await confirmBox(`Approve ${stageName("pps")} for ${pps} colours at once?`, "Approve"))) return;
  const body: any = { cells, status, date: v.get("date") || null, comment };
  let r: any;
  try {
    try { r = await post<any>("/api/org/sampling/bulk", body); }
    catch (err) {
      if (!(err instanceof ApiError) || err.code !== "FABRIC_NO_REPORT") throw err;
      if (!(await warnNoReport(err.message, err.details?.lines || []))) return;
      r = await post<any>("/api/org/sampling/bulk", { ...body, confirm: true });
    }
  } catch (err) { return message((err as Error).message); }
  toast(r.skipped.length ? `${r.summary}. Not changed: ${r.skipped.map((x: any) => `${x.line} ${x.stage} (${x.reason})`).join("; ")}` : `${r.summary}.`, r.skipped.length && !r.changed ? "error" : "ok");
  sel.clear();
  await load();
});
onAction("sg-undo", async (el) => {
  try { const r = await post<any>(`/api/org/sampling/bulk/${el.dataset.id}/undo`, {}); toast(`Undone: ${r.summary}.`); await load(); }
  catch (err) { toast((err as Error).message, "error"); }
});

// ---- The panel under the table: a stage's rounds, or the bulk fabric lots ------------------------------------------
onAction("sg-open", async (el) => {
  const lineId = el.dataset.line!, stage = el.dataset.stage as Stage;
  if (open && open.lineId === lineId && open.stage === stage) return closePanel();
  await openPanel(lineId, stage);
});
onAction("sg-panel-close", () => closePanel());
function closePanel() {
  open = null;
  const box = document.getElementById("sg-panel");
  if (box) box.innerHTML = "";
  host?.querySelectorAll(".sg-open").forEach((b) => { b.classList.remove("sg-open"); b.setAttribute("aria-expanded", "false"); });
  host?.querySelectorAll("tr.sg-focus").forEach((t) => t.classList.remove("sg-focus"));
}
async function openPanel(lineId: string, stage: Stage, quiet = false) {
  const r = rowOf(lineId), box = document.getElementById("sg-panel");
  if (!r || !box) return;
  open = { lineId, stage };
  host?.querySelectorAll(".sg-open").forEach((b) => { b.classList.remove("sg-open"); b.setAttribute("aria-expanded", "false"); });
  const btn = host?.querySelector<HTMLElement>(`.sg-badge[data-line="${CSS.escape(lineId)}"][data-stage="${stage}"]`);
  btn?.classList.add("sg-open"); btn?.setAttribute("aria-expanded", "true");
  host?.querySelectorAll("tr.sg-focus").forEach((t) => t.classList.remove("sg-focus"));
  host?.querySelector(`tr[data-line="${CSS.escape(lineId)}"]`)?.classList.add("sg-focus");
  if (stage === "fabric") await renderLotsPanel(box, r.poId, lineId, async () => { await load(); });
  else if (stage === "labdip") await renderLabDipPanel(box, lineId, async () => { await load(); });
  else await renderRounds(box, r, stage);
  if (!quiet) box.scrollIntoView({ block: "nearest" });
}

const d2 = (v?: string | null) => (v ? formatDate(v) : "");
async function renderRounds(box: HTMLElement, row: any, stage: Stage) {
  let r: any;
  try { r = await get(`/api/org/pos/${row.poId}/samples/${row.lineId}/${stage}`); } catch (err) { box.innerHTML = `<div class="empty">${esc((err as Error).message)}</div>`; return; }
  const body = !r.rounds.length ? '<div class="empty">No rounds yet. Tick the status above and Apply to start round 1.</div>'
    : r.rounds.map((x: any) => `<div class="ps-round"><div class="ps-round-head"><strong>${esc(x.tag)}</strong> <span class="cp-badge tone-${x.status === "approved" ? "ok" : x.status === "rejected" ? "bad" : "amber"}">${esc(x.statusText)}</span>${x.autoCreated ? ' <span class="muted">opened automatically</span>' : ""}
${x.canCorrect ? `<button type="button" class="link-btn" data-action="sg-correct" data-id="${esc(x.id)}" data-now="${esc(x.status)}" data-tag="${esc(x.tag)}">Change status</button>` : ""}</div>
<dl class="ps-facts">${[["Requested", d2(x.requested)], ["Planned", d2(x.planned)], ["Sent", d2(x.sent)], ["Received", d2(x.received)], ["Reviewed", `${d2(x.reviewed)}${x.reviewedBy ? ` · ${x.reviewedBy}` : ""}`], ["Tech Pack", x.techpack]].filter(([, v]) => v).map(([k, v]) => `<div><dt>${k}</dt><dd>${esc(v)}</dd></div>`).join("")}</dl>
${x.rejectionReason ? `<p><strong>Rejection reason:</strong> ${esc(x.rejectionReason)}</p>` : ""}${x.comments ? `<p class="falcon-prewrap">${esc(x.comments)}</p>` : ""}${x.photos.length ? `<p>${x.photos.map((f: any) => `<a href="/api/org/files/${esc(f.id)}" target="_blank" rel="noopener">${esc(f.name)}</a>`).join(" · ")}</p>` : ""}
${x.log.length ? `<details><summary>History (${x.log.length})</summary><ul class="ps-log">${x.log.map((e: any) => `<li>${esc(formatDateTime(e.at))} · ${esc(e.by)} · ${e.fromText ? `${esc(e.fromText)} → ` : ""}${esc(e.toText)}${e.kind === "correction" ? " <strong>(correction)</strong>" : e.kind === "bulk" ? " (applied in Sampling)" : e.kind === "auto" ? " (automatic)" : e.kind === "undo" ? " (undo)" : ""}${e.reason ? ` — ${esc(e.reason)}` : ""}</li>`).join("")}</ul></details>` : ""}</div>`).join("");
  box.innerHTML = `<section class="sg-panel ps-details"><div class="sg-panel-head"><h4>${esc(r.stageName)} · ${esc(row.poNumber)} · ${esc(row.code)}${row.colour ? ` ${esc(row.colour)}` : ""}</h4>
<div class="toolbar"><button type="button" class="secondary small-btn" data-action="sg-details" data-line="${esc(row.lineId)}" data-type="${stage}">Dates, courier, photos${stage === "pps" ? ` & ${esc(data.names?.seal || "gold seal")}` : ""}</button><button type="button" class="secondary small-btn" data-action="sg-panel-close">Close</button></div></div>${body}</section>`;
}
onAction("sg-details", (el) => openSample?.(el.dataset.line!, el.dataset.type!));

const CHOICES: Array<[string, string]> = [["waiting", "Requested"], ["in_transit", "Sent"], ["received", "Received"], ["under_review", "In review"], ["approved", "Approved"], ["rejected", "Rejected"]];
onAction("sg-correct", (el) => {
  openModal(`Change status · ${el.dataset.tag}`, `<form data-form="sg-correct" data-id="${esc(el.dataset.id)}" novalidate><div class="form-message" role="alert" hidden></div>
<p class="muted">Change the status of this one round, e.g. to correct a mistake. The history keeps who, when, from → to and your reason.</p>
<label class="field"><span>New status</span><select name="status">${CHOICES.filter(([k]) => k !== el.dataset.now).map(([k, l]) => `<option value="${k}">${esc(l)}</option>`).join("")}</select></label>
<label class="field"><span>Reason</span><input name="reason" required maxlength="1000" placeholder="e.g. Clicked Rejected by mistake — buyer approved"></label>
<div class="form-actions"><button type="button" class="secondary" data-action="close-modal">Cancel</button><button type="submit" class="primary">Change status</button></div></form>`);
});
onForm("sg-correct", async (form) => {
  const v = new FormData(form), url = `/api/org/sampling/rounds/${form.dataset.id}/status`;
  const body: any = { status: v.get("status"), reason: String(v.get("reason") || "") };
  try {
    try { await post(url, body); }
    catch (err) {
      if (!(err instanceof ApiError) || err.code !== "NEXT_ROUND_HAS_DATA") throw err;
      const remove = await confirmBox(`${err.message}\n\nChoose "Remove it" to remove that round too, or Cancel to keep it.`, "Remove it", true);
      await post(url, { ...body, nextRound: remove ? "remove" : "keep" });
    }
    closeModal();
    toast("Status changed. The change is in the history.");
    await load();
  } catch (err) { formError(form, err); }
});
